import { useState, useEffect, useCallback } from 'react';
import { PAGESIZE } from '../constantes/constantes';
import { getSearch } from '../services/getSearch';
import Loading from './loading/Loading';
import SearchForm from './searchForm/searchForm';
import Results from './results/results';
import Pagination from './pagination/pagination';
import PaginationMobile from './pagination/paginationMobile';
import ErrorService from './error/errorService';
import Unauthorized from './error/unauthorized';

export default function BuscadorApp() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [showLoading, setShowLoading] = useState(true);
  const [pathURL, setPathURL] = useState('');
  const [searchParamsChanged, setSearchParamsChanged] = useState(false);
  const [isDomainAllowed, setIsDomainAllowed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);
  const [searchError, setSearchError] = useState(false);
  const [serviceError, setServiceError] = useState(null);

  /**
   * Detecta si el dispositivo es móvil basado en el ancho de pantalla
   * 
   * @description Actualiza el estado isMobile evaluando si el ancho de la ventana
   * es menor o igual a 850px. Esta función es utilizada como callback en el
   * event listener de resize para adaptar la interfaz responsivamente.
   */
  const isMobileDevice = () => {
    setIsMobile(window.innerWidth <= 850);
  };

  /**
   * Configura la detección responsiva de dispositivos móviles
   * 
   * @description Registra un event listener para el evento 'resize' que actualiza
   * automáticamente el estado isMobile cuando cambia el tamaño de la ventana.
   * Se ejecuta solo al montar el componente y limpia el listener al desmontarlo.
   */
  useEffect(() => {
    window.addEventListener('resize', isMobileDevice);

    return () => {
      window.removeEventListener('resize', isMobileDevice);
    };
  }, []);

  /**
   * Maneja la paginación tipo "cargar más" en dispositivos móviles
   * 
   * @description Incrementa la página actual en 1 y marca que los parámetros
   * han cambiado para disparar una nueva búsqueda. Los resultados se agregan
   * a los existentes creando un efecto de scroll infinito.
   */
  const handlePageChangeMobile = () => {
    setPage((prevPage) => prevPage + 1);
    setSearchParamsChanged(true);
  };

  /**
   * Valida que el dominio actual esté autorizado para usar el widget
   * 
   * @description Verifica si el hostname actual pertenece a los dominios permitidos:
   * clarosports.com, unotv.com, amxinfra o localhost. Actualiza el estado
   * isDomainAllowed que determina si se muestra el widget o el componente
   * Unauthorized. Se ejecuta solo al montar el componente.
   */
  useEffect(() => {
    const domain = window.location.hostname;
    if (domain) {
      const isAllowed =
        domain.includes("clarosports.com") ||
        domain.endsWith("clarosports.com") ||
        domain.includes('amxinfra') ||
        domain.includes("unotv.com") ||
        domain.endsWith("unotv.com") ||
        domain.includes("localhost");
      setIsDomainAllowed(isAllowed);
    }
  }, []);

  /**
   * Sincroniza el estado inicial con los parámetros de la URL
   * 
   * @description Lee los parámetros de búsqueda de la URL (q, category, page)
   * y actualiza los estados correspondientes solo si están vacíos. Esto permite
   * que el widget mantenga el estado de búsqueda al recargar la página o al
   * recibir parámetros del iframe padre. Se ejecuta solo al montar el componente.
   */
  useEffect(() => {
    if (!query && !category && page === 0) {
      const urlParams = new URLSearchParams(window.location.search);
      setQuery(urlParams.get('q') || '');
      setCategory(urlParams.get('category') || '');
      setPage(parseInt(urlParams.get('page'), 10) || 0);
      setSearchParamsChanged(true);
    }
  }, []);

  /**
   * Ejecuta la búsqueda principal con manejo de estados y errores
   * 
   * @description Función principal que realiza la búsqueda de contenido.
   * Actualiza la URL del navegador, llama al servicio de búsqueda y maneja
   * diferentes tipos de respuesta. En móvil agrega resultados (scroll infinito),
   * en desktop reemplaza los datos. Gestiona estados de carga y errores.
   */
  const performSearch = useCallback(async () => {
    if (!query.trim()) {
      setShowLoading(false);
      return;
    }

    try {
      setShowLoading(true);

      const url = new URL(window.location.href);
      url.searchParams.set('q', query);
      url.searchParams.set('category', category);
      url.searchParams.set('page', page);
      window.history.pushState({}, '', url);

      const search = await getSearch(query, page, PAGESIZE, category);

      if (search?.statusCode === 200) {
        setSearchError(false);
        setServiceError(null);

        if (isMobile) {
          setData(prevData => {
            const safePrevData = prevData || { hits: [] };
            const newHits = Array.isArray(search.data.hits) ? search.data.hits : [];
            const areHitsEqual = safePrevData.hits.length > 0 && newHits.length > 0 && safePrevData.hits[0].title === newHits[0].title;

            return {
              from: search.data.from,
              total: search.data.total,
              hits: areHitsEqual ? [...safePrevData.hits] : [...safePrevData.hits, ...newHits]
            };
          });
        } else {
          setData(search.data);
        }
      } else if (search?.statusCode === 500) {
        setSearchError(true);
        setServiceError(null);
      } else if (search?.statusCode === 503) {
        setServiceError(search.error || 'Servicio temporalmente no disponible');
        setSearchError(false);
      } else {
        setData([]);
        setServiceError(null);
      }
    } catch (error) {
      console.error('Error en búsqueda:', error);
      setSearchError(true);
    } finally {
      setShowLoading(false);
    }
  }, [query, category, page, isMobile]);

  /**
   * Ejecuta la búsqueda cuando cambian los parámetros
   * 
   * @description Orquesta la ejecución de búsquedas cuando se detectan cambios
   * en los parámetros. Realiza la búsqueda, envía parámetros al iframe padre
   * y actualiza la altura del contenedor. Solo se ejecuta si no hay errores
   * de búsqueda activos.
   */
  useEffect(() => {
    if (searchParamsChanged && !searchError) {
      setSearchParamsChanged(false);
      performSearch();
      sendParams({ q: query, categoria: category, from: page });
      setTimeout(sendHeight, 500);
    }
  }, [searchParamsChanged, searchError, performSearch, query, category, page]);

  /**
   * Reinicia los datos para nuevas búsquedas en dispositivos móviles
   * 
   * @description Limpia los datos existentes cuando se inicia una nueva búsqueda
   * (query o categoría) en la primera página y en modo móvil. Esto evita que
   * se muestren resultados anteriores mientras se cargan los nuevos en el
   * patrón de scroll infinito móvil.
   */
  useEffect(() => {
    if ((query || category) && page === 0 && isMobile) {
      setData(null);
    }
  }, [isMobile, query, category, page])

  /**
   * Maneja el cambio del término de búsqueda
   * 
   * @description Actualiza el query de búsqueda, reinicia la página a 0
   * y marca que los parámetros han cambiado para disparar una nueva búsqueda.
   * Se ejecuta cuando el usuario modifica el texto en el campo de búsqueda.
   */
  const handleQueryChange = (query) => {
    // Validar tipo y sanitizar entrada
    if (typeof query !== 'string') {
      console.warn('Query debe ser string');
      return;
    }

    // Sanitizar entrada
    const sanitizedQuery = query.trim().slice(0, 500); // Límite de caracteres

    // Validar caracteres peligrosos
    const dangerousChars = /<script|javascript:|data:/i;
    if (dangerousChars.test(sanitizedQuery)) {
      console.warn('Query contiene caracteres no permitidos');
      return;
    }

    setQuery(query);
    setPage(0);
    setSearchParamsChanged(true);
  };

  /**
   * Maneja el cambio de categoría en el filtro de búsqueda
   * 
   * @description Actualiza la categoría seleccionada, reinicia la página a 0
   * y marca que los parámetros han cambiado para disparar una nueva búsqueda.
   * Se ejecuta cuando el usuario selecciona una opción diferente en el dropdown.
   */
  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
    setPage(0);
    setSearchParamsChanged(true);
  };

  /**
   * Ejecuta una nueva búsqueda reiniciando la paginación
   * 
   * @description Reinicia la página a 0 y marca que los parámetros han cambiado
   * para disparar una nueva búsqueda. Se utiliza cuando el usuario presiona
   * Enter o hace clic en el botón de búsqueda del formulario.
   */
  const handleSearch = () => {
    setPage(0);
    setSearchParamsChanged(true);
  };

  /**
   * Maneja el cambio de página en la paginación desktop
   * 
   * @description Actualiza la página actual y marca que los parámetros de búsqueda
   * han cambiado para disparar una nueva búsqueda. Esta función es utilizada
   * por el componente Pagination para navegar entre páginas de resultados.
   * 
   * @param {number} newPage - Número de la nueva página (0-indexed)
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParamsChanged(true);
  };

  /**
   * Reinicia el formulario de búsqueda y limpia todos los parámetros
   * 
   * @description Restablece todos los estados del buscador a sus valores iniciales:
   * query vacío, categoría vacía, página 0 y datos nulos. También limpia los
   * parámetros de búsqueda de la URL del navegador para mantener la sincronización
   * entre el estado de la aplicación y la URL.
   */
  const handleReset = () => {
    setQuery('');
    setCategory('');
    setPage(0);
    setData(null);

    // Limpiar querystrings de la URL
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    url.searchParams.delete('category');
    url.searchParams.delete('page');
    window.history.pushState({}, '', url);
  };

  /**
   * Envía la altura del contenedor al iframe padre para ajuste automático
   * 
   * @description Calcula la altura actual del contenedor principal y la envía
   * al iframe padre mediante postMessage. Esto permite que el sitio padre ajuste
   * dinámicamente la altura del iframe según el contenido del widget.
   */
  const sendHeight = () => {
    if (pathURL) {
      const container = document.querySelector('.container');
      if (container) {
        const height = container.offsetHeight;
        const data = { type: 'resize', height };
        window.parent.postMessage(data, pathURL);
      }
    }
  };

  /**
   * Envía los parámetros de búsqueda al iframe padre para sincronización
   * 
   * @description Comunica los parámetros actuales de búsqueda (query, categoría, página)
   * al sitio padre mediante postMessage. Esto permite mantener sincronizada la URL
   * del sitio padre con el estado actual del widget de búsqueda.
   */
  const sendParams = (params) => {
    if (pathURL) {
      const data = { type: 'params', params };
      window.parent.postMessage(data, pathURL);
    }
  };

  /**
   * Configura listeners para actualizar la altura del iframe automáticamente
   * 
   * @description Registra event listeners para los eventos 'load' y 'resize' del window
   * que ejecutan sendHeight para mantener actualizada la altura del iframe padre.
   * Se ejecuta cuando pathURL cambia y limpia los listeners al desmontar el componente
   * para evitar memory leaks.
   */
  useEffect(() => {
    window.addEventListener('load', sendHeight);
    window.addEventListener('resize', sendHeight);

    return () => {
      window.removeEventListener('load', sendHeight);
      window.removeEventListener('resize', sendHeight);
    };
  }, [pathURL]);

  /**
   * Validación de seguridad por dominio
   * 
   * @description Verifica si el dominio actual está autorizado para usar el widget.
   * Si el dominio no está en la lista de permitidos (clarosports.com, unotv.com,
   * localhost), muestra el componente Unauthorized y bloquea el acceso al buscador.
   */
  if (isDomainAllowed === false) {
    return (
      <Unauthorized />
    );
  }

  console.log("log search...")
  return (
    <div className="container">
      <SearchForm
        query={query}
        setQuery={setQuery}
        category={category}
        onQueryChange={handleQueryChange}
        onCategoryChange={handleCategoryChange}
        onSearch={handleSearch}
        onReset={handleReset}
        isMobile={isMobile}
      />

      {showLoading && <Loading />}

      {serviceError && <ErrorService />}

      {!showLoading && !serviceError && <Results data={data} query={query} />}

      {data && data.total > PAGESIZE && !showLoading && !serviceError &&
        <>
          {!isMobile ?
            <Pagination
              currentPage={page}
              total={data ? data.total : 0}
              pageSize={PAGESIZE}
              onPageChange={handlePageChange}
            />
            :
            <PaginationMobile
              currentPage={page}
              total={data ? data.total : 0}
              pageSize={PAGESIZE}
              onPageChange={handlePageChangeMobile}
              loadedItems={data ? data.hits?.length || 0 : 0}
            />
          }
        </>
      }
    </div>
  );
};